import Vue from 'vue';
import { MutationTree } from 'vuex';
import { AuthState } from '../types';
import { applyCurrencyToI18n, getCountryFromAddress, getCurrencyFromCountry } from '@/utils/currency';

const tenantFromRestaurant = (r: Restaurant): Omit<Tenant, 'id' | 'csv_import'> => ({
	name: r.name,
	locales: r.locales,
	pos_integrated: r.pos_integrated,
	slug: r.slug,
	group: r.group,
	groups: r.groups ?? null,
	orders_paused: r.orders_paused,
	order_options: r.order_options
});

export const mutations: MutationTree<AuthState> = {

	SET_TENANT_ID(state: AuthState, tenantId: string) {
		Vue.set(state, 'tenantId', tenantId);
	},

	SET_AXIOS_INSTANCE(state: AuthState, axiosInst: string) {
		Vue.set(state, 'axiosInst', axiosInst);
	},

	SET_RESTAURANT(state, restaurant: Restaurant) {
		applyCurrencyToI18n(getCurrencyFromCountry(getCountryFromAddress(restaurant.address)));
		Vue.set(state, 'restaurant', restaurant);

		// Keep the current selected tenant up-to-date
		if (state.restaurants?.length) {
			const index = state.restaurants.findIndex((t: Tenant) => t.id === restaurant.id);

			if (index > -1) {
				Vue.set(state.restaurants, index, {
					...state.restaurants[index],
					...tenantFromRestaurant(restaurant)
				});
			}
		}
	},

	SET_RESTAURANTS(state: AuthState, restaurants: string) {
		Vue.set(state, 'restaurants', JSON.parse(restaurants));
	},

	SET_SWITCHING_LOCATION(state: AuthState, switchingLocation: boolean) {
		Vue.set(state, 'switchingLocation', switchingLocation);
	},

	/**
	 * Normalize Auth0's `app_metadata.groups` to a sorted, deduped string[] (or null).
	 * Auth0 can emit either a comma-separated string or an array depending on the metadata shape;
	 * we handle both here so the rest of the app can trust `state.authGroups` as `string[] | null`.
	 *
	 * @param {AuthState} state
	 * @param {string | string[] | null | undefined}
	 */
	SET_AUTH_GROUPS(state: AuthState, groups: string | string[] | null | undefined) {
		if (!groups) {
			Vue.set(state, 'authGroups', null);
			return;
		}
		const raw: string[] = Array.isArray(groups) ? groups : String(groups).split(',');
		const normalized: string[] = Array.from(new Set(
			raw.map((g: string) => (g || '').trim()).filter((g: string) => g.length > 0)
		)).sort((a: string, b: string) => a.localeCompare(b));
		Vue.set(state, 'authGroups', normalized.length ? normalized : null);
	},

	/**
	 * Set the current role assigned to the user in its app_metadata.
	 * For backwards compatibility purposes, if the role is empty we
	 * will give them manager access. Unsurprisingly Auth0 does not have
	 * a bulk API edit for users. Prior locations had the enterprise privileges
	 * on their account without the actual role.
	 *
	 * @param {AuthState} state
	 * @param {Role} role
	 */
	SET_ROLE(state: AuthState, role: Role) {
		Vue.set(state, 'role', role ?? 'manager');
	},

	SET_ACCESS_TOKEN(state: AuthState, accessToken: string) {
		Vue.set(state, 'accessToken', accessToken);
	},

	SET_LAST_LOGIN(state: AuthState, lastLogin: string) {
		Vue.set(state, 'lastLogin', lastLogin);
	},

	SET_ID_TOKEN(state: AuthState, idToken: string) {
		Vue.set(state, 'idToken', idToken);
	},

	SET_EXPIRES_AT(state: AuthState, expiresAt: string) {
		Vue.set(state, 'expiresAt', expiresAt);
	},

	SET_AUTHENTICATED(state: AuthState, authenticated: boolean) {
		Vue.set(state, 'authenticated', authenticated);
	},

	SET_POS_INTEGRATED(state: AuthState) {
		Vue.set(state, 'posIntegrated', state.restaurant.pos_integrated);
	},

	SET_STAFF_EMAIL(state: AuthState, email: string) {
		Vue.set(state, 'staffEmail', email);
	},

	SET_EMPLOYEE_ID(state: AuthState, id: string) {
		Vue.set(state, 'employeeId', id);
	},

	SET_EMPLOYEE_NAME(state: AuthState, name?: string | null) {
		Vue.set(state, 'employeeName', name ?? null);
	},

	SET_CREATED_BY(state: AuthState, id?: string | null) {
		Vue.set(state, 'createdBy', id ?? null);
	},

	/**
	 * Set the current role assigned to the staff member accessing the menu manager
	 * via a pin. This differs from the base role which is assigned to the logged in user.
	 * A 'manager' user can login and allow multiple staff members to use the app, while
	 * separating permissions based on the pin of the staff member.
	 * If no role is assigned to the pin, we assume full permissions for backwards compatibility.
	 *
	 * @param {AuthState} state
	 * @param {Role} pinRole
	 */
	SET_PIN_ROLE(state: AuthState, pinRole: PinRole) {
		Vue.set(state, 'pinRole', pinRole ?? 'admin');
	}
};
